/**
 * Style With (complementary products) tracking.
 *
 * 1. Click tracking (PDP): publishes a custom 'style_with_click' event to
 *    Shopify Customer Events, picked up by the GTM custom pixel -> GA4.
 *    Delegated on document because the widget's content is fetched and
 *    swapped in by <product-recommendations>.
 *
 * 2. Order attribution (sitewide): remembers clicked handles for the session
 *    and, when one of those products is in the cart, writes a hidden
 *    '_style_with' cart attribute (e.g. "clicked-handle<source-handle, ...").
 *    It lands on the order under "Additional details". Synced after every
 *    cart fetch (add/change/update) and on page load.
 */
(() => {
  if (window.__lyStyleWithTracking) return;
  window.__lyStyleWithTracking = true;

  const STORAGE_KEY = 'ly_style_with_clicks';
  const ATTRIBUTE = '_style_with';
  const MAX_STORED = 20;
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  const readClicks = () => {
    try {
      return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || [];
    } catch (e) {
      return [];
    }
  };

  const saveClick = (clicked, source) => {
    if (!clicked) return;
    try {
      const clicks = readClicks().filter((c) => c.clicked !== clicked);
      clicks.push({ clicked, source: source || '' });
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(clicks.slice(-MAX_STORED)));
    } catch (e) {
      /* storage unavailable — attribution just won't happen */
    }
  };

  // ---------- 1. Click tracking ----------
  document.addEventListener('click', (event) => {
    const widget = event.target.closest('product-recommendations[data-style-with-tracking]');
    if (!widget) return;

    const link = event.target.closest('a[href*="/products/"]');
    if (!link || !widget.contains(link)) return;

    const items = Array.from(widget.querySelectorAll('.complementary-slider li'));
    const item = link.closest('li');
    // Image links have no id/text, so fall back to the card's title link
    const titleLink = (item && item.querySelector('a[id^="CardLink-"], a[id^="StandardCardNoMediaLink-"]')) || link;
    const idMatch = (link.id || titleLink.id || '').match(/-(\d+)$/);
    const handleMatch = link.pathname.match(/\/products\/([^/?#]+)/);

    const payload = {
      source_product_id: widget.dataset.productId || null,
      source_product_handle: widget.dataset.productHandle || null,
      clicked_product_id: idMatch ? idMatch[1] : null,
      clicked_product_handle: handleMatch ? handleMatch[1] : null,
      clicked_product_title: (titleLink.textContent || '').trim(),
      position: item ? items.indexOf(item) + 1 : null,
      widget_heading: widget.dataset.widgetHeading || null,
    };

    saveClick(payload.clicked_product_handle, payload.source_product_handle);

    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish('style_with_click', payload);
      }
    } catch (e) {
      /* never block navigation on tracking */
    }
  });

  // ---------- 2. Order attribution ----------
  const originalFetch = window.fetch.bind(window);
  let syncing = false;

  // Attribute format: "clicked<source, clicked<source"
  const parseAttr = (value) =>
    (value || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => {
        const [clicked, source] = s.split('<');
        return { clicked, source: source || '' };
      });

  const serialise = (entries) => entries.map((e) => (e.source ? `${e.clicked}<${e.source}` : e.clicked)).join(', ');

  const syncCartAttribute = async () => {
    if (syncing) return;
    syncing = true;
    try {
      const clicks = readClicks();
      const cart = await originalFetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const existingValue = (cart.attributes && cart.attributes[ATTRIBUTE]) || '';
      if (!clicks.length && !existingValue) return;

      const inCart = new Set(cart.items.map((i) => i.handle));
      const merged = new Map();
      [...parseAttr(existingValue), ...clicks].forEach((e) => {
        if (e.clicked && inCart.has(e.clicked) && !merged.has(e.clicked)) merged.set(e.clicked, e);
      });

      const nextValue = serialise([...merged.values()]);
      if (nextValue === existingValue) return;

      await originalFetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [ATTRIBUTE]: nextValue } }),
      });
    } catch (e) {
      /* attribution is best-effort */
    } finally {
      syncing = false;
    }
  };

  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const promise = originalFetch(input, init);
    if (CART_MUTATION.test(url.split('#')[0])) {
      promise.then(() => setTimeout(syncCartAttribute, 0)).catch(() => {});
    }
    return promise;
  };

  // Catch adds that didn't go through fetch (e.g. native form post -> /cart)
  if (readClicks().length) syncCartAttribute();
})();
